import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';

export default function PaintingMaintenance() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      <div className="pt-20 pb-20">
        {/* Hero */}
        <div className="relative h-[400px] md:h-[500px] mb-32 overflow-hidden rounded-[12px] mx-6 md:mx-12">
          <img
            src="https://pim-b2c.maxbo.no/_default_upload_bucket/maxbo/Innhold/Artikler/image-thumb__52833__b2c-image-brick/Utemaling_milj%C3%B8_2_1600x900.png"
            alt="Vedlikehold av malt badstue"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent flex flex-col justify-end p-12">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white mb-6">Vedlikehold</p>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light leading-[1.1] text-white mb-6">
                Vedlikeholdsplan <span className="italic">Over Tid</span>
              </h1>
              <p className="font-body text-white/80 text-lg max-w-2xl leading-relaxed">
                En strukturert oversikt over vedlikehold av din malte badstue gjennom årene for optimal beskyttelse og estetikk.
              </p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Maintenance Timeline */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mb-32">
            <p className="text-sm tracking-widest-2xl font-body uppercase text-primary mb-6">Oversikt</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-[1.1] mb-16 text-[#2a1810]">Vedlikeholdsplan <span className="italic">Over Tid</span></h2>
            
            <Accordion type="single" collapsible className="space-y-2">
              {[
                { year: 'År 1', title: 'Observasjon', items: ['Sjekk malingens adhesjon', 'Overvåk sprekker', 'Skånsom rengjøring'] },
                { year: 'År 2–3', title: 'Vedlikehold', items: ['Skånsom rengjøring', 'Inspeksjon av malingslaget', 'Små reparasjoner'] },
                { year: 'År 7–10', title: 'Oppfriskning', desc: 'Rengjøring og maling av områder med sprekker.' },
                { year: 'År 15–20', title: 'Full Overflatebehandling', desc: 'Komplett ny maling for optimal beskyttelse og estetikk.' }
              ].map((phase, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="border border-border/20 rounded-[8px] px-6">
                  <AccordionTrigger className="py-4 hover:no-underline text-left">
                    <div className="flex items-start gap-4">
                      <span className="text-2xl font-heading font-light text-[#2d5a3d]/60 flex-shrink-0">{phase.year}</span>
                      <h3 className="font-heading text-lg font-light text-[#2a1810]">{phase.title}</h3>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-4 pt-0">
                    {phase.items ? (
                      <ul className="space-y-2">
                        {phase.items.map((item, i) => (
                          <li key={i} className="font-body text-base text-foreground/70 flex items-start gap-2">
                            <Check size={14} className="text-[#2d5a3d] flex-shrink-0 mt-1" /> {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="font-body text-base text-foreground/70">{phase.desc}</p>
                    )}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.section>

          {/* Tips */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="mb-32">
            <h2 className="font-heading text-3xl font-light leading-[1.1] mb-8 text-[#2a1810]">Praktiske <span className="italic">Tips</span></h2>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                { title: 'Regelmessig Rengjøring', desc: 'Vask badstuen med mildt såp og vann hvert 2–3 år for å fjerne alger og fuktflekker.' },
                { title: 'Inspeksjon', desc: 'Sjekk årlig for sprekker, skalleflak og andre tegn på slitasje, spesielt etter winter.' },
                { title: 'Reparasjoner', desc: 'Små reparasjoner bør gjøres umiddelbart for å hindre at problemet utvikler seg.' },
                { title: 'Dokumentasjon', desc: 'Dokumenter vedlikehold for å holde oversikt over hva som er gjort og når.' }
              ].map((tip, idx) => (
                <motion.div key={idx} whileHover={{ y: -4 }} className="bg-white rounded-[8px] p-8 border border-border/20 hover:border-[#2d5a3d] transition-colors">
                  <h3 className="font-heading text-lg font-light mb-4 text-[#2a1810]">{tip.title}</h3>
                  <p className="font-body text-foreground/70">{tip.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* CTA */}
          <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="bg-gradient-to-br from-[#2a1810] to-[#3d2818] text-white rounded-[6px] p-12 md:p-20 text-center">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <p className="text-sm tracking-widest-2xl font-body uppercase text-white/60 mb-6">Neste Steg</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light leading-[1.2] mb-6">
                Klar for<br /><span className="italic">Din Malte Badstue?</span>
              </h2>
              <p className="font-body text-lg text-white/70 mb-12 max-w-2xl mx-auto">
                Har du spørsmål om vedlikehold eller er klar til å begynne? Vi hjelper deg med hele prosessen fra fargeutvalg til ferdig badstue.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="/be-om-tilbud"
                  className="px-8 py-4 bg-white text-[#2a1810] font-body tracking-widest text-sm font-semibold rounded-[4px] hover:bg-white/90 transition-all inline-block">
                  BE OM TILBUD
                </motion.a>
              </div>
            </motion.div>
          </motion.section>
        </div>
      </div>

      <Footer />
    </div>
  );
}